/**
 * Authentication API Service
 *
 * Handles all authentication-related API calls including login, logout,
 * session management, and CSRF token handling.
 *
 * All requests include credentials: 'include' for cookie-based session management.
 */

import type { AuthUserResponse, LoginResponse, ChangePasswordRequest } from '../../../shared/types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Get CSRF token from cookie
 * Always reads from cookie to ensure token is available after page refresh
 */
export function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  const csrfCookie = cookies.find(c => c.trim().startsWith('csrf_token='));

  if (csrfCookie) {
    return csrfCookie.split('=')[1];
  }

  return null;
}

export function setCsrfToken(_token: string | null): void {
  // No-op: CSRF token is managed via cookie by the server
}

export function clearCsrfToken(): void {
  // No-op: CSRF token cookie is cleared by the server on logout
}

/**
 * Custom error class for authentication errors
 */
export class AuthApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

/**
 * Handle API response and extract JSON or throw appropriate error
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { message: 'An error occurred' } }));
    // Error response format: { success: false, error: { message: "...", code: "..." } }
    const errorData = body.error || body;
    throw new AuthApiError(
      response.status,
      errorData.message || 'An error occurred',
      errorData.code
    );
  }
  return response.json();
}

/**
 * Get common headers for requests
 * Includes CSRF token for state-changing operations
 */
function getHeaders(includeCSRF: boolean = false): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (includeCSRF) {
    const token = getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  return headers;
}

/**
 * Authentication API methods
 */
export const authApi = {
  /**
   * Login with email and password
   * On success, returns user object and sets session cookie
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: getHeaders(true), // Include CSRF if available (for re-login scenarios)
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    return handleResponse<LoginResponse>(response);
  },

  /**
   * Logout current user
   * Clears session cookie server-side
   */
  async logout(): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: getHeaders(true),
      credentials: 'include',
    });

    // Clear CSRF token on logout
    clearCsrfToken();

    return handleResponse<{ success: boolean }>(response);
  },

  /**
   * Get current authenticated user from session
   * Used to restore session on page load
   */
  async getMe(): Promise<{ success: boolean; user?: AuthUserResponse }> {
    const response = await fetch(`${API_BASE}/auth/me`, {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    });

    return handleResponse<{ success: boolean; user?: AuthUserResponse }>(response);
  },

  /**
   * Fetch CSRF token from cookie
   * The CSRF token is set as a non-httpOnly cookie during login
   * This is now a no-op since getCsrfToken() reads directly from cookie
   */
  fetchCsrfToken(): string {
    return getCsrfToken() || '';
  },

  /**
   * Change password for current user
   * Requires current password for verification
   */
  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean }> {
    const body: ChangePasswordRequest = { currentPassword, newPassword };

    const response = await fetch(`${API_BASE}/auth/password`, {
      method: 'PATCH',
      headers: getHeaders(true),
      credentials: 'include',
      body: JSON.stringify(body),
    });

    return handleResponse<{ success: boolean }>(response);
  },
};

export default authApi;
